import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from 'lucide-react'

interface BlogPost {
    id: string
    title: string
    excerpt: string
    content: string
    category: string
    image: string
    author: string
    authorRole: string
    tags: string[]
    date: string
    readTime: string
    isPublished: boolean
}

const BLOG_CATEGORIES = ['NEET Strategy', 'Biology Tips', 'Physics & Chemistry', 'Foreign MBBS', 'Student Life', 'Success Stories', 'Study Hacks']
const BLOG_TAGS = ['NEET 2025', 'Biology', 'Physics', 'Chemistry', 'Study Plan', 'Time Management', 'Mock Tests', 'Motivation', 'Foreign MBBS', 'Coaching', 'Ranker Tips']

const defaultBlogs: BlogPost[] = [
    {
        id: 'ab1',
        title: 'How I Scored 700+ in NEET: A Complete Strategy Breakdown',
        excerpt: 'In this post, I\'ll share exactly how I prepared for 12 months, the resources I used, and the mindset shifts that made all the difference.',
        content: '',
        category: 'Success Stories',
        image: 'https://images.unsplash.com/photo-1513258496099-48168024aec0?w=600&h=380&fit=crop',
        author: 'Dr. Priya Sharma',
        authorRole: 'NEET 2024 Topper · AIR 245',
        tags: ['NEET 2025', 'Study Plan', 'Ranker Tips'],
        date: '2025-03-18',
        readTime: '10 min read',
        isPublished: true,
    },
    {
        id: 'ab2',
        title: 'MBBS Abroad or India: A Brutally Honest 2025 Comparison',
        excerpt: 'We break down costs, risks, career outcomes, and the NMC screening exam reality for students choosing between Indian and foreign MBBS.',
        content: '',
        category: 'Foreign MBBS',
        image: 'https://images.unsplash.com/photo-1562774053-701939374585?w=600&h=380&fit=crop',
        author: 'Arjun Reddy',
        authorRole: 'Education Counsellor · Future Founder',
        tags: ['Foreign MBBS', 'Coaching'],
        date: '2025-03-11',
        readTime: '12 min read',
        isPublished: true,
    },
]

const emptyForm = {
    title: '', excerpt: '', content: '', category: BLOG_CATEGORIES[0],
    image: '', author: '', authorRole: '', tags: [] as string[],
    date: new Date().toISOString().split('T')[0], readTime: '5 min read', isPublished: true,
}

export function AdminBlogsView() {
    const [posts, setPosts] = useState<BlogPost[]>(defaultBlogs)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({ ...emptyForm })
    const [tagInput, setTagInput] = useState('')
    const [filterCategory, setFilterCategory] = useState('All')

    const openCreate = () => {
        setEditingId(null)
        setForm({ ...emptyForm, tags: [] })
        setTagInput('')
        setModalOpen(true)
    }

    const openEdit = (p: BlogPost) => {
        setEditingId(p.id)
        setForm({
            title: p.title, excerpt: p.excerpt, content: p.content,
            category: p.category, image: p.image, author: p.author,
            authorRole: p.authorRole, tags: [...p.tags],
            date: p.date, readTime: p.readTime, isPublished: p.isPublished,
        })
        setTagInput('')
        setModalOpen(true)
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setPosts(prev => prev.map(p => p.id === editingId ? { ...p, ...form } : p))
        } else {
            setPosts(prev => [...prev, { ...form, id: `ab-${Date.now()}` }])
        }
        setModalOpen(false)
    }

    const handleDelete = (id: string) => {
        if (confirm('Delete this blog post?')) setPosts(prev => prev.filter(p => p.id !== id))
    }

    const togglePublish = (id: string) => {
        setPosts(prev => prev.map(p => p.id === id ? { ...p, isPublished: !p.isPublished } : p))
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, image: url }))
        }
    }

    const addTag = (tag: string) => {
        const trimmed = tag.trim()
        if (trimmed && !form.tags.includes(trimmed)) {
            setForm(f => ({ ...f, tags: [...f.tags, trimmed] }))
        }
        setTagInput('')
    }

    const removeTag = (tag: string) => {
        setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))
    }

    const filtered = filterCategory === 'All' ? posts : posts.filter(p => p.category === filterCategory)

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Blog Management</h2>
                    <p className="text-sm text-theme-secondary mt-1">Create and manage blog posts shown on the public Blog page</p>
                </div>
                <button
                    onClick={openCreate}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors"
                >
                    <Plus className="h-5 w-5" /> Add Blog Post
                </button>
            </div>

            {/* Filter bar */}
            <div className="flex gap-2 flex-wrap">
                {['All', ...BLOG_CATEGORIES].map(cat => (
                    <button
                        key={cat}
                        onClick={() => setFilterCategory(cat)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${filterCategory === cat ? 'bg-primary-blue text-white' : 'text-theme-secondary bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-primary-blue">{posts.length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Total Posts</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">{posts.filter(p => p.isPublished).length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Published</p>
                </GlassmorphicCard>
                <GlassmorphicCard className="p-4 text-center">
                    <p className="text-2xl font-bold text-slate-500">{posts.filter(p => !p.isPublished).length}</p>
                    <p className="text-xs text-theme-secondary mt-1">Drafts</p>
                </GlassmorphicCard>
            </div>

            {/* Posts grid */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map(p => (
                    <GlassmorphicCard key={p.id} className="overflow-hidden">
                        <div className="relative h-36 overflow-hidden">
                            {p.image ? (
                                <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-200 dark:from-indigo-900/30 dark:to-purple-800/30 flex items-center justify-center">
                                    <span className="text-theme-muted text-sm">No Image</span>
                                </div>
                            )}
                            <div className="absolute top-2 left-2">
                                <span className="px-2 py-0.5 bg-primary-blue text-white text-xs font-semibold rounded-full">{p.category}</span>
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1">
                                <button onClick={() => togglePublish(p.id)} className={`p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 ${p.isPublished ? 'text-green-600' : 'text-slate-400'}`}>
                                    {p.isPublished ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                </button>
                                <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-theme-primary">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="font-semibold text-theme-primary text-sm leading-snug line-clamp-2 mb-1">{p.title}</h3>
                            <p className="text-xs text-theme-muted mb-1">{p.author} · {p.readTime}</p>
                            <div className="flex flex-wrap gap-1 mb-2">
                                {p.tags.slice(0, 2).map(t => (
                                    <span key={t} className="px-1.5 py-0.5 bg-primary-blue/10 text-primary-blue text-xs rounded">{t}</span>
                                ))}
                                {p.tags.length > 2 && <span className="text-xs text-theme-muted">+{p.tags.length - 2}</span>}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-theme-muted">{p.date}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.isPublished ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                                    {p.isPublished ? 'Published' : 'Draft'}
                                </span>
                            </div>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-lg p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit Blog Post' : 'New Blog Post'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Title *</label>
                                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    placeholder="Blog post title..." required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Excerpt / Summary *</label>
                                <textarea value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    rows={2} placeholder="Short summary shown in blog listings..." required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Full Content</label>
                                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    rows={6} placeholder="Write the full blog post content here..." />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Category *</label>
                                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" required>
                                        {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Read Time</label>
                                    <input value={form.readTime} onChange={e => setForm(f => ({ ...f, readTime: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="5 min read" />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Author Name</label>
                                    <input value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="Dr. Name" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Author Role</label>
                                    <input value={form.authorRole} onChange={e => setForm(f => ({ ...f, authorRole: e.target.value }))}
                                        className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="Physics Mentor · Future Founder" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Publish Date</label>
                                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                                    className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Cover Image</label>
                                <input type="file" accept="image/*" onChange={handleImageUpload}
                                    className="w-full text-sm text-theme-secondary file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-blue/10 file:text-primary-blue" />
                                <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))}
                                    className="w-full mt-2 px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                    placeholder="Or paste image URL..." />
                                {form.image && <img src={form.image} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
                            </div>
                            {/* Tags */}
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Tags</label>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {form.tags.map(t => (
                                        <span key={t} className="flex items-center gap-1 px-2.5 py-1 bg-primary-blue/10 text-primary-blue text-xs rounded-full">
                                            {t}
                                            <button type="button" onClick={() => removeTag(t)} className="text-primary-blue/60 hover:text-red-500">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                <div className="flex gap-2">
                                    <select value="" onChange={e => { if (e.target.value) addTag(e.target.value) }}
                                        className="flex-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm">
                                        <option value="">Select a tag to add...</option>
                                        {BLOG_TAGS.filter(t => !form.tags.includes(t)).map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                    <input
                                        value={tagInput}
                                        onChange={e => setTagInput(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput) } }}
                                        className="flex-1 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary text-sm"
                                        placeholder="Custom tag + Enter" />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={form.isPublished} onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Publish on public Blogs page</span>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary text-sm">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold text-sm">Save Post</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
